import type {PropsWithChildren, ReactNode} from 'react';
import React, {
  useCallback,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import type {GridRowSelectionModel} from '@mui/x-data-grid';
import type {
  DataGridProProps,
  GridApiPro,
  GridColDef,
  GridInitialState,
  GridRowParams,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import {DataGridPro} from '@mui/x-data-grid-pro';
import {
  DEFAULT_PAGINATION_MODEL,
  type RowId,
  type RowSelectionMode,
} from './types';
import {CustomFooter, CustomGridToolbar, NoRowsOverlay} from './components';
import {
  applyColumnWidths,
  filterPanelProps,
  getTableStyles,
  orderColumns,
} from './functions';

export type DataTableProps<TData extends GridValidRowModel> = {
  apiRef: React.RefObject<GridApiPro | null>;
  className?: string;
  loading?: boolean;
  columns: GridColDef[];
  rows: TData[];
  enableSelectionButton?: RowSelectionMode;
  selectionModel?: GridRowSelectionModel;
  onSelectionChanged?: (model: GridRowSelectionModel) => void;
  onRowClick?: (row: TData) => void;
  initialState?: GridInitialState;
  onSaveState?: (state: GridInitialState) => void;
  getRowHeight?: (params: {
    id: RowId;
    densityFactor: number;
  }) => number | undefined | 'auto';
  header?: ReactNode;
  columnSelectionTitle?: string;
  pageSizeAllowAll?: boolean;
  onRowsUpdate?: (gridRows: GridValidRowModel[]) => void;
  scrollAfterPagination?: boolean;
} & Pick<
  DataGridProProps<TData>,
  | 'hideFooter'
  | 'slots'
  | 'slotProps'
  | 'pinnedColumns'
  | 'rowCount'
  | 'disableColumnResize'
  | 'onPaginationModelChange'
  | 'onSortModelChange'
  | 'onFilterModelChange'
  | 'onColumnVisibilityModelChange'
>;

/**
 * https://mui.com/x/react-data-grid/layout/#flex-parent-container
 */
const tableSx: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: '175px',
};

export function DataTable<TData extends GridValidRowModel>({
  apiRef,
  className,
  loading,
  columns,
  rows,
  enableSelectionButton,
  selectionModel,
  onSelectionChanged,
  onRowClick,
  initialState,
  onSaveState,
  getRowHeight,
  slots,
  slotProps,
  header,
  columnSelectionTitle,
  pageSizeAllowAll,
  hideFooter,
  pinnedColumns,
  onRowsUpdate,
  rowCount,
  scrollAfterPagination,
  disableColumnResize,
  onPaginationModelChange,
  onSortModelChange,
  onFilterModelChange,
  onColumnVisibilityModelChange,
}: PropsWithChildren<DataTableProps<TData>>) {
  const [gridInitialState] = useState<GridInitialState>(() => ({
    ...initialState,
    columns: {
      // orderedFields and dimensions is handled via columns-prop instead.
      columnVisibilityModel: initialState?.columns?.columnVisibilityModel,
    },
    pagination: {
      ...initialState?.pagination,
      paginationModel: {
        ...DEFAULT_PAGINATION_MODEL,
        ...initialState?.pagination?.paginationModel,
      },
      rowCount,
    },
  }));

  const orderedFields = initialState?.columns?.orderedFields;
  const dimensions = initialState?.columns?.dimensions;
  const gridColumns = useMemo(
    () => applyColumnWidths(orderColumns(columns, orderedFields), dimensions),
    [columns, orderedFields, dimensions],
  );

  const saveState = useEffectEvent(() => {
    if (onSaveState && apiRef.current?.exportState) {
      onSaveState(apiRef.current.exportState());
    }
  });

  // Layout effect, so that state is saved on unmount before apiRef is cleared
  useLayoutEffect(() => {
    const handleBeforeUnload = () => saveState();
    const handleVisibilityChange = () => {
      if (document.hidden) {
        saveState();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      saveState();
    };
  }, []);

  const handleSelectionChanged = useCallback(
    (model: GridRowSelectionModel) => {
      if (onSelectionChanged) {
        onSelectionChanged(model);
      }
    },
    [onSelectionChanged],
  );

  const handleRowClick = useCallback(
    (params: GridRowParams) => {
      onRowClick && onRowClick(params.row as TData);
    },
    [onRowClick],
  );

  const sx = useMemo(() => {
    return getTableStyles<TData>(onRowClick, getRowHeight);
  }, [getRowHeight, onRowClick]);

  const pageSizeOptions = useMemo(() => {
    const allOption = -1;
    const allowAllGreaterThanLimit = pageSizeAllowAll ? [allOption] : [];

    return [20, 50, 100, 200, ...allowAllGreaterThanLimit];
  }, [pageSizeAllowAll]);

  return (
    <div style={tableSx}>
      <DataGridPro<TData>
        apiRef={apiRef}
        initialState={gridInitialState}
        pagination={true}
        paginationMode={rowCount !== undefined ? 'server' : 'client'}
        rowCount={rowCount}
        pinnedColumns={pinnedColumns}
        className={className}
        loading={loading}
        columns={gridColumns}
        rows={rows}
        sx={sx}
        density="compact"
        rowSpacingType="margin"
        getRowHeight={getRowHeight}
        rowSelectionPropagation={{parents: false, descendants: false}}
        hideFooter={hideFooter}
        showToolbar
        rowSelection={
          enableSelectionButton === 'single' ||
          enableSelectionButton === 'multiple'
        }
        rowSelectionModel={selectionModel}
        disableMultipleRowSelection={enableSelectionButton === 'single'}
        disableRowSelectionOnClick={true}
        onRowSelectionModelChange={handleSelectionChanged}
        checkboxSelection={
          enableSelectionButton === 'single' ||
          enableSelectionButton === 'multiple'
        }
        onRowClick={handleRowClick}
        disableColumnResize={disableColumnResize}
        onPaginationModelChange={onPaginationModelChange}
        onSortModelChange={onSortModelChange}
        onFilterModelChange={onFilterModelChange}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        disableColumnMenu={true}
        pageSizeOptions={pageSizeOptions}
        slots={{
          noRowsOverlay: NoRowsOverlay,
          toolbar: CustomGridToolbar,
          footer: CustomFooter,
          ...slots,
        }}
        slotProps={{
          ...slotProps,
          toolbar: {
            header: header,
            columnSelectionTitle,
            onRowsUpdate,
            scrollAfterPagination,
            ...slotProps?.toolbar,
          },
          footer: {
            scrollAfterPagination,
          },
          basePopper: {
            placement: 'bottom-end',
          },
          filterPanel: filterPanelProps,
        }}
      />
    </div>
  );
}
